<template>
  <div
    class="table-wrapper fire-marshal__contacts"
  >
    <div class="table-caption">
      {{ $t('tenancy.table.fireMarshalContacts') }}
    </div>
    <div
      v-if="allowLinkContacts"
      class="link__contact"
    >
      <button
        type="button"
        class="btn link__contact--btn"
        :disabled="!tenancyId"
        @click="linkFunc({isFireMarshal: true})"
      >{{ $t('tenancy.table.linkContact') }}</button>
      <span class="link__contact--plus">+</span>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :key="index"
            :class="header.class"
          >{{ header.name }}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in contacts"
          :key="index"
        >
          <td>
            <div v-if="canAccessContact">
              <a
                class="name-hyper-link"
                @click="navToContactDetail(item.id, true)"
              >{{ item.fullName || item.name }}</a>
              <i
                v-if="isEngineer"
                v-show="!!item.remarks"
                class="remark-included-flag"
              />
            </div>
            <div v-else>
              {{ item.fullName || item.name }}
            </div>
          </td>
          <td>
            {{ item.phone | isEmptyValue }}
          </td>
          <td class="select-emails">
            <HKLForm ref="formRef">
              <HKLMultiSelect
                v-fusioninputcheck="{
                  type: 'string',
                  required: true,
                  showTip: false,
                }"
                :value="item.fEmails"
                :options="item.allEmails"
                @change="val => updateEmail(item.index, val)"
              />
            </HKLForm>
          </td>
          <td class="select-role">
            <HKLSelect
              :value="item.i18nRole"
              :options="roleOpts"
              @select="val => updateRole(item.index, val, item.fireMarshalRole)"
            />
          </td>
          <td class="input-remarks">
            <HKLTextarea
              :value="item.fRemarks"
              :disabled="!allowLinkContacts"
              @onChange="val => updateRemarks(item.index, val)"
            />
          </td>
          <td class="icon-remove">
            <i
              v-if="allowLinkContacts"
              class="fa fa-times-thin"
              @click="() => openDelModal(item.index)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <HKLModal
      ref="delConfirmModal"
      okBtnAction="yes"
      @onOkClick="removeContact()"
    >
      <div slot="title">
        {{ $t('deleteConfirmMsg') }}
      </div>
    </HKLModal>
  </div>
</template>

<script>
import { computed, ref, unref, watchEffect } from 'vue-demi'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
import HKLModal from '@/controls/Modal/HKLModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { utils as formUtils } from '@/plugin/InputChange'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreGetters, useStoreMutations } from '@/store/index'
import mutationTypes from '@/store/mutation-types'
import i18n from '@/utils/i18n'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import HKLForm from '@/controls/Form/HKLForm.vue'
import { useRefForm } from '@/controls/Form/hook'
import { FIRE_MARSHAL_ROLE } from '../consts'
import { useFireMarshalContacts, useFireMarshalHeader } from '../hook'
import { useTenancyContactComputes } from './hook'
import navToContactDetailMixin from './navToContactDetailMixin'

export default {
  components: {
    HKLForm,
    HKLModal,
    HKLSelect,
    HKLMultiSelect,
    HKLTextarea,
  },
  props: {
    allowLinkContacts: {
      type: Boolean,
      default: false,
    },
    linkFunc: {
      type: Function,
      default: () => {},
    },
  },
  setup () {
    const tenancyId = useRouteParamId()
    const { canAccessContact } = useTenancyContactComputes()
    const { navToContactDetail } = navToContactDetailMixin.methods
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const { isEngineer, isTenant } = useStoreGetters('user', ['isEngineer', 'isTenant'])
    const { updateTenancyContactPathField } = useStoreMutations('ccd', {
      updateTenancyContactPathField: mutationTypes.TENANCY_CONTACT_UPDATE_PATH_FIELD,
    })
    const updatePathField = (path, index, val) => {
      updateTenancyContactPathField({
        index: index,
        path: [path],
        newValue: val,
      })
      formUtils.setDirty()
    }
    const updateEmail = (index, val) => {
      updatePathField('fEmails', index, val)
    }
    const updateRemarks = (index, val) => {
      updatePathField('fRemarks', index, val)
    }
    const updateRole = (index, val, oldVal) => {
      updatePathField('fireMarshalRole', index, val)
      updateSelectedOpts(oldVal, val)
    }
    const updateSelectedOpts = (oldVal, newVal) => {
      const selected = selectedOpts.value
      const removingIndex = selected.indexOf(oldVal)
      const addingIndex = selected.indexOf(newVal)
      if (removingIndex > -1) {
        selected.splice(removingIndex, 1)
      }
      if (addingIndex === -1 && newVal) {
        selected.push(newVal)
      }
    }
    const itemHeaders = useFireMarshalHeader(true)
    const selectedOpts = ref([])
    const contacts = useFireMarshalContacts()
    const roleOpts = computed(() => {
      const result = Object.values(FIRE_MARSHAL_ROLE).map(val => {
        return {
          name: i18n.t(`tenancy.fireMarshalRole.${val}`),
          value: val,
        }
      }).filter(item => {
        return !selectedOpts.value.includes(item.value)
      })
      result.push({
        name: i18n.t('emptyValue'),
        value: '',
      })
      return result
    })
    watchEffect(() => {
      selectedOpts.value = unref(contacts).map(item => item.fireMarshalRole).filter(val => !!val)
    })
    const deletingContactIndex = ref(-1)
    const { modal: delConfirmModal, open: openDelConfirmModal } = useRefModal()
    const removeContact = () => {
      const index = unref(deletingContactIndex)
      updatePathField('isFireMarshal', index, false)
      updateRole(index, '')
      deletingContactIndex.value = -1
    }
    const openDelModal = (index) => {
      deletingContactIndex.value = index
      openDelConfirmModal()
    }
    const { form: formRef, validate } = useRefForm()
    return {
      formRef,
      validate,
      deletingContactIndex,
      tenancyId,
      isTenant,
      isEngineer,
      itemHeaders,
      roleOpts,
      contacts,
      currentTenancy,
      delConfirmModal,
      selectedOpts,
      openDelModal,
      updateEmail,
      updateRemarks,
      updateRole,
      removeContact,
      canAccessContact,
      navToContactDetail,
    }
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/table';
@import '~styles/components/tenancy-contacts';
.fire-marshal__contacts {
  .select-emails {
    width: 25%;
  }
  ::v-deep .input-remarks textarea {
    margin-bottom: 0;
  }
}
</style>
